import { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { translations, SUPPORTED_LOCALES } from '@/i18n/translations';
import { api } from '@/lib/api';

const LocaleContext = createContext(null);

const detectDefault = () => {
  const saved = localStorage.getItem('locale');
  if (saved && translations[saved]) return saved;
  const nav = (navigator.language || 'fr').slice(0, 2).toLowerCase();
  if (translations[nav]) return nav;
  return 'fr';
};

// Set the initial html lang immediately so browser translation stays inactive
if (typeof document !== 'undefined') {
  const initial = detectDefault();
  document.documentElement.lang = initial;
  document.documentElement.setAttribute('translate', 'no');
}

export function LocaleProvider({ children, user }) {
  const [locale, setLocaleState] = useState(detectDefault());

  useEffect(() => {
    if (user?.locale && translations[user.locale] && user.locale !== locale) {
      setLocaleState(user.locale);
      localStorage.setItem('locale', user.locale);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.locale]);

  const setLocale = useCallback(async (code) => {
    if (!translations[code]) return;
    setLocaleState(code);
    localStorage.setItem('locale', code);
    document.documentElement.lang = code;
    try { await api.put('/me/locale', { locale: code }); } catch {}
  }, []);

  const t = useCallback((key, params) => {
    const dict = translations[locale] || translations.fr;
    let s = dict[key] || translations.fr[key] || key;
    if (params) {
      Object.entries(params).forEach(([k, v]) => {
        s = s.replace(`{${k}}`, String(v));
      });
    }
    return s;
  }, [locale]);

  return (
    <LocaleContext.Provider value={{ locale, setLocale, t, supported: SUPPORTED_LOCALES }}>
      {children}
    </LocaleContext.Provider>
  );
}

export const useT = () => useContext(LocaleContext) || { t: (k) => k, locale: 'fr', setLocale: () => {}, supported: SUPPORTED_LOCALES };
